import { useState } from 'react';
import { Paperclip } from 'lucide-react';
import { formatBusinessDate } from '@ampersandbrands/ui';
import { HtmlBody } from '@/components/HtmlBody';
import { usePublishedContent, type ContentItem } from '@/lib/useContent';
import { apiGet } from '@/lib/api';
import { API_BASE } from '@/lib/apiBase';
import { useMarkAreaSeen } from '@/lib/useHomeSummary';

type TaggedItem = ContentItem & { _label?: string };

function AttachmentButton({ itemId, att }: { itemId: number; att: { id: number; filename: string } }) {
  const [busy, setBusy] = useState(false);

  async function open() {
    setBusy(true);
    try {
      const { url } = await apiGet<{ url: string }>(`${API_BASE}/v1/content/published/${itemId}/attachments/${att.id}`);
      window.open(url, '_blank', 'noopener');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      className="inline-flex items-center gap-1 text-xs text-candy-teal underline-offset-2 hover:underline disabled:opacity-50 dark:text-candy-teal-dark"
    >
      <Paperclip className="h-3 w-3" aria-hidden />
      {busy ? 'Opening…' : att.filename}
    </button>
  );
}

function ContentCard({ item }: { item: TaggedItem }) {
  const fields = item.fields;
  const startDate = typeof fields?.start_date === 'string' ? fields.start_date : undefined;
  const endDate = typeof fields?.end_date === 'string' ? fields.end_date : undefined;
  const skus = Array.isArray(fields?.skus) ? (fields!.skus as string[]) : null;

  return (
    <div className="rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      {item._label && (
        <span className="mb-2 inline-block rounded-pill bg-candy-lavender px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white dark:bg-candy-lavender-dark">
          {item._label}
        </span>
      )}
      {(startDate || endDate) && (
        <p className="mb-1 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {startDate ? formatBusinessDate(startDate) : ''}
          {startDate && endDate ? ' – ' : ''}
          {endDate ? formatBusinessDate(endDate) : ''}
        </p>
      )}
      <h2 className="mb-1.5 text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">{item.title}</h2>
      {item.body_html && <HtmlBody src={item.body_html} />}
      {skus && skus.length > 0 && (
        <p className="mt-2 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          SKUs: {skus.join(', ')}
        </p>
      )}
      {item.attachments.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {item.attachments.map((att) => (
            <AttachmentButton key={att.id} itemId={item.id} att={att} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Promotions() {
  useMarkAreaSeen('promotions');
  const promoQ = usePublishedContent('promotions');
  const mktQ = usePublishedContent('marketing');

  const isLoading = promoQ.isLoading || mktQ.isLoading;

  const items: TaggedItem[] = [
    ...(promoQ.data ?? []),
    ...(mktQ.data ?? []).map((item) => ({ ...item, _label: 'Marketing' })),
  ].sort((a, b) => {
    const at = a.published_at ? new Date(a.published_at).getTime() : 0;
    const bt = b.published_at ? new Date(b.published_at).getTime() : 0;
    return bt - at;
  });

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Promotions</h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">Current campaigns &amp; marketing</p>
      </div>

      {isLoading && (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Nothing here yet — HQ content is coming.
          </p>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="space-y-4">
          {items.map((item) => (
            <ContentCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
